import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  useToast,
} from '@chakra-ui/react'
import type { AxiosError } from 'axios'
import type {
  ChangePasswordRequest,
  ModelState
} from 'BrokerTsLib/src/dtos'
import {
  BadRequestError
} from 'BrokerTsLib/src/dtos'
import { useChangePassword } from 'BrokerTsLib/src/hooks/useAccount'
import { FormProvider, useForm } from 'BrokerTsLib/src/rhf'
import { setModelStateErrors } from 'BrokerTsLib/src/Utils/formModelState'
import { useNavigate } from 'react-router'

import { FormGroup } from '../../components/FormGroup'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { InputGroup } from '../../components/InputGroup'

export const ChangePasswordForm = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const form = useForm<ChangePasswordRequest>({
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    reValidateMode: 'onChange',
    mode: 'onTouched',
  })
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = form

  const { mutateAsync: changePassword, isPending } = useChangePassword()
  const submitHandler = (data: ChangePasswordRequest) =>
    changePassword(data)
      .then(() => {
        toast({
          title: 'Heslo bylo změněno',
          status: 'success',
        })
        navigate('/profile')
      })
      .catch((e: AxiosError) => {
        if (e.response?.status === 400)
          setModelStateErrors(
            setError,
            new BadRequestError(e.response.data as ModelState<ChangePasswordRequest>),
          )
        else
          toast({
            title: 'Chyba při změně hesla',
            description: `${e.response?.status}: ${e.response?.statusText}`,
            status: 'error',
          })
      })

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <FormSection>
          <FormSectionHeader>
            <FormSectionTitle>Aktuální heslo</FormSectionTitle>

            <FormSectionDescription>
              Zadejte své aktuální heslo pro ověření
            </FormSectionDescription>
          </FormSectionHeader>
          <FormSectionBody>
            <FormGroup>
              <FormControl isInvalid={!!errors.oldPassword}>
                <FormLabel>Heslo</FormLabel>

                <InputGroup
                  id="oldPassword"
                  type="password"
                  placeholder="Aktuální heslo"
                  errors={errors.oldPassword}
                  {...register('oldPassword', {
                    required: 'Zadejte aktuální heslo',
                    minLength: 8,
                  })}
                />
              </FormControl>
            </FormGroup>
          </FormSectionBody>
        </FormSection>
        <FormSection>
          <FormSectionHeader>
            <FormSectionTitle>Nové heslo</FormSectionTitle>

            <FormSectionDescription>
              Zvolte si nové heslo
            </FormSectionDescription>
          </FormSectionHeader>
          <FormSectionBody>
            <FormGroup>
              <FormControl isInvalid={!!errors.newPassword}>
                <FormLabel>Nové heslo</FormLabel>

                <InputGroup
                  id="newPassword"
                  type="password"
                  placeholder="Nové heslo"
                  errors={errors.newPassword}
                  {...register('newPassword', {
                    required: 'Zadejte nové heslo',
                    minLength: {
                      value: 8,
                      message: 'Heslo musí mít alespoň 8 znaků',
                    },
                    validate: {
                      differentFromOld: (value) =>
                        value !== form.getValues('oldPassword') ||
                        'Nové heslo musí být odlišné od starého',
                      requireDigit: (value) =>
                        /\d/.test(value) ||
                        'Heslo musí obsahovat alespoň jednu číslici',
                      requireNonLetterOrDigit: (value) =>
                        /\W/.test(value) ||
                        'Heslo musí obsahovat alespoň jeden speciální znak',
                      requireLowercase: (value) =>
                        /[a-z]/.test(value) ||
                        'Heslo musí obsahovat alespoň jedno malé písmeno',
                    },
                  })}
                />
              </FormControl>
              <FormControl isInvalid={!!errors.confirmPassword}>
                <FormLabel>Potvrzení nového hesla</FormLabel>

                <InputGroup
                  id="confirmPassword"
                  type="password"
                  placeholder="Nové heslo"
                  errors={errors.confirmPassword}
                  {...register('confirmPassword', {
                    deps: ['newPassword'],
                    validate: {
                      sameAsNewPassword: (value) =>
                        value === form.getValues('newPassword') ||
                        'Hesla se neshodují',
                    },
                  })}
                />
              </FormControl>
            </FormGroup>
          </FormSectionBody>
        </FormSection>
        <Flex>
          <Button
            ml={'auto'}
            type="submit"
            colorScheme="primary"
            isDisabled={!form.formState.isValid}
            isLoading={isPending}
          >
            Změnit heslo
          </Button>
        </Flex>
      </form>
    </FormProvider>
  )
}
